import type { ApiClient } from './client';
import type { CodeBatch, Provenance } from './codeBatches';

/**
 * Demo samples, as `veri-plus-backend` serves them, Q5 and D35.
 *
 * `POST /v1/demo/samples` takes a `productId`, `payloads` (1 to 1000 strings
 * of up to 500 characters) and an optional `provenance`, and answers
 * `{ batch, created, duplicates }`: 201 when anything was created, 200 with
 * `batch: null` when every payload was already registered. A payload that
 * already reads as a Veri+ code is a 422 keyed `body.payloads.N`.
 * `POST /v1/demo/samples/reset` clears the first scan on the org's demo
 * samples only and answers `{ reset: n }`. Both are org admin, and both are
 * 403 `demo_not_enabled` unless the org is in demo mode. Backend
 * `docs/products-codes-api.md`, Demo samples. D36.
 */

export const demoSamplesPath = '/v1/demo/samples';
export const demoResetPath = '/v1/demo/samples/reset';

export interface SamplesInput {
  productId: string;
  payloads: string[];
  provenance?: Provenance;
}

export interface Registered {
  batch: CodeBatch | null;
  created: number;
  duplicates: number;
}

export function registerSamples(api: ApiClient, input: SamplesInput): Promise<Registered> {
  return api.post<Registered>(demoSamplesPath, input);
}

export async function resetDemoScans(api: ApiClient): Promise<number> {
  const answer = await api.post<{ reset: number }>(demoResetPath, {});
  return answer.reset;
}

/**
 * One payload per line, blank lines dropped, each trimmed as the API trims.
 * A line that is one quoted CSV field is unquoted, so a spreadsheet's export
 * of a single column reads as typed.
 */
export function toPayloads(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .map((line) =>
      line.length >= 2 && line.startsWith('"') && line.endsWith('"')
        ? line.slice(1, -1).replace(/""/g, '"')
        : line,
    )
    .filter((line) => line !== '');
}

/** `body.payloads.3` is the fourth payload sent: its line number among the payloads, from one. */
export function payloadIndex(path: string): number | null {
  const match = /^(?:body\.)?payloads\.(\d+)$/.exec(path);
  return match ? Number(match[1]) : null;
}

/** What a register answered, as one line. */
export function registeredText({ created, duplicates }: Registered): string {
  const samples = (n: number) => `${n.toLocaleString('en-US')} sample${n === 1 ? '' : 's'}`;
  if (created === 0) {
    return duplicates === 1
      ? 'That sample was already registered.'
      : `All ${samples(duplicates)} were already registered.`;
  }
  const already = duplicates > 0 ? `, ${duplicates.toLocaleString('en-US')} already were` : '';
  return `${samples(created)} registered${already}.`;
}
